import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";

import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL } from "../../style";
import WebsiteMediaInput from "./WebsiteMediaInput";

export default function WebsiteNotificationImages() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/notification-images",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Notification Images");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Notification Images" />
            <TabHeader
                className="px-3 pt-3"
                title="Notification Images"
                highlights={["Add, edit, and delete rows"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: {} })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/notification-images/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Notification Images Deleted", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    const active = true;
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.id}. {row.url || "Untitled"}</span>
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_active: true, is_published: true, ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const save = () => {
        requestAPI({
            requestPath: "website/notification-images",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Notification Images Saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add" : "Edit"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                    <FloatLabel className="mt-5">
                        <InputText id="notification_public_id" className="w-full" value={record?.notification_public_id ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, notification_public_id: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="notification_public_id">Notification Public Id</label>
                    </FloatLabel>
                    <WebsiteMediaInput label="Photo" field="url" hint="Required size: 1280 ? 720 px (16:9). A larger photo is scaled to fit. The whole photo stays visible on the notice page." record={record} setRecord={setRecord} disabled={saving} />
                    <FloatLabel className="mt-5">
                        <InputTextarea id="alt_text" className="w-full" rows={4} autoResize value={record?.alt_text || ""} onChange={(event) => setRecord((prev) => ({ ...prev, alt_text: event.target.value }))} disabled={saving} />
                        <label htmlFor="alt_text">Alt Text</label>
                    </FloatLabel>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
